import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect } from 'react';
import { Card, DatePicker, Button, Table, Form, Space, Modal, Spin } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, ReloadOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { useFetchAllFactures } from '../../hooks/factureHooks';
import dayjs from 'dayjs';
import ReactDOM from 'react-dom';
import axios from 'axios';

const { RangePicker } = DatePicker;

// HTML Template for the journal report
const journalTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Journal des Ventes M3</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 10mm;
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 10pt;
      line-height: 1.2;
      color: #000;
      background-color: #fff;
      margin: 0;
      padding: 0;
    }
    .print-page {
      width: 190mm;
      /* A4 leaves 277mm after margins; allow 1mm for print rounding. */
      min-height: 276mm;
      box-sizing: border-box;
      display: flow-root; /* Keep the heading margin inside the page. */
      padding: 0;
      position: relative;
      page-break-inside: avoid;
      break-inside: avoid-page;
    }
    .print-page + .print-page {
      page-break-before: always;
      break-before: page;
    }
    h1 {
      font-size: 14pt;
      text-align: center;
      margin: 10px 0 15px 0;
    }
    .print-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 15px;
      font-size: 10pt;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    th, td {
      border: 0.5pt solid #000;
      padding: 3px 4px;
      text-align: left;
      font-size: 9pt;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    th {
      background-color: #f0f0f0;
      font-weight: bold;
    }
    .print-footer {
      position: absolute;
      bottom: 10mm;
      width: 100%;
      font-size: 9pt;
      text-align: center;
      margin-top: 10px;
    }
    /* Column widths */
    th:nth-child(1), td:nth-child(1) { width: 12%; } /* Date */
    th:nth-child(2), td:nth-child(2) { width: 14%; } /* Ref */
    th:nth-child(3), td:nth-child(3) { width: 14%; } /* Compte */
    th:nth-child(4), td:nth-child(4) { width: 36%; } /* Client */
    th:nth-child(5), td:nth-child(5) { width: 16%; } /* Ville */
    th:nth-child(6), td:nth-child(6) { width: 8%; }  /* Mode paie */
  </style>
</head>
<body>
  {{PAGES}}
</body>
</html>`;

// Template for each page
const pageTemplate = `
<div class="print-page">
  {{PAGE_HEADER}}
  <table>
    <thead>
      <tr>
        <th>Date Fac.</th>
        <th>Ref. Fac.</th>
        <th>Compte</th>
        <th>Client</th>
        <th>Ville</th>
        <th>M paie</th>
      </tr>
    </thead>
    <tbody>
      {{ROWS}}
    </tbody>
  </table>
  <div class="print-footer">
    <div>Total des factures: {{TOTAL_INVOICES}}</div>
    <div class="page-number">Page {{CURRENT_PAGE}} / {{TOTAL_PAGES}}</div>
  </div>
</div>`;

const FactureJournal = ({ onClose }) => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [showReport, setShowReport] = useState(false);
  const [sorter, setSorter] = useState({ field: 'DATE_FAC', order: 'ascend' });
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [counters, setCounters] = useState({ facture: {}, tree: {} });
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;

  // Fetch parameters
  const params = useMemo(() => ({
    page: 1,
    pageSize: 1000, // Large number to show all results
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: 'validated', // Show only validated factures
  }), [dateRange, sorter]);

  const { factures, loading, refetch } = useFetchAllFactures(params);

  // Fetch counters when component mounts
  useEffect(() => {
    fetchCounters();
  }, []);

  const fetchCounters = async () => {
    try {
      const response = await axios.get(`${API_URL}/counter/getcountercheck`);
      setCounters(response.data);
    } catch (error) {
      console.error('Failed to fetch counters:', error);
    }
  };

  // Extract the first 3 digits from REF_FAC (after the first 2 characters)
  const extractTreeDigits = (refFac) => {
    if (!refFac || refFac.length < 5) return ''; // Need at least 5 chars (2 prefix + 3 digits)
    return refFac.substring(2, 5); // Extract only 3 digits after the prefix
  };

  // Extract the first 3 digits from counter value
  const extractFirstThreeDigits = (value) => {
    if (!value) return '';
    const valueStr = String(value);
    return valueStr.substring(0, 3);
  };

  // Determine the 1/3 value based on matching tree digits
  const determineOneThirdValue = (refFac) => {
    if (!refFac || !counters.tree || !counters.facture) return '';
    
    const treeDigitsFromRef = extractTreeDigits(refFac);
    const treeFirstThreeDigits = extractFirstThreeDigits(counters.tree.value);
    const factureFirstThreeDigits = extractFirstThreeDigits(counters.facture.value);
    
    if (treeDigitsFromRef === treeFirstThreeDigits) {
      return '3';
    } else if (treeDigitsFromRef === factureFirstThreeDigits) {
      return '1';
    } else {
      return '';
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setShowReport(false);
  };

  const generateReport = () => {
    if (dateRange[0] && dateRange[1]) {
      setShowReport(true);
      refetch();
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSorter({
        field: sorter.field,
        order: sorter.order
      });
    }
  };

  // Process template with replacements
  const processTemplate = (template, replacements) => {
    let processedTemplate = template;
    Object.entries(replacements).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processedTemplate = processedTemplate.replace(regex, value || '');
    });
    return processedTemplate;
  };

  // Generate pages for the report
  const generatePages = (factures, fromDate, toDate) => {
    const rowsPerPage = 40;
    const groups = { '1': [], '3': [], '': [] };
    factures.forEach(record => {
      groups[determineOneThirdValue(record.REF_FAC)].push(record);
    });

    // Paginate each category separately so category 3 always starts a new page.
    // Keep unclassified invoices on separate pages rather than omitting them.
    const pages = [];
    ['1', '3', ''].forEach(category => {
      const sortedFactures = groups[category].sort((a, b) =>
        String(a.REF_FAC || '').localeCompare(String(b.REF_FAC || ''), 'fr', { numeric: true })
      );
      for (let start = 0; start < sortedFactures.length; start += rowsPerPage) {
        pages.push(sortedFactures.slice(start, start + rowsPerPage));
      }
    });
    if (pages.length === 0) pages.push([]);
    const totalPages = pages.length;
    let pagesHtml = '';
    
    for (let page = 0; page < totalPages; page++) {
      const pageFactures = pages[page];
      
      // Generate rows HTML
      let rowsHtml = '';
      pageFactures.forEach(record => {
        rowsHtml += `
          <tr>
            <td>${dayjs(record.DATE_FAC).format('DD/MM/YYYY')}</td>
            <td>${record.REF_FAC || ''}</td>
            <td>${record.COMPTE || ''}</td>
            <td>${record.CLIENT || ''}</td>
            <td>${record.client?.VILLE || ''}</td>
            <td>${record.MODE_PAIE || ''}</td>
          </tr>
        `;
      });
      
      // Generate header for first page only
      const pageHeader = page === 0 ? `
        <h1>Journal des Ventes M3 ${dayjs().format('YYYY')}</h1>
        <div class="print-header">
        </div>
      ` : '';
      
      // Process page template
      pagesHtml += processTemplate(pageTemplate, {
        PAGE_HEADER: pageHeader,
        ROWS: rowsHtml,
        CURRENT_PAGE: page + 1,
        TOTAL_PAGES: totalPages,
        TOTAL_INVOICES: factures.length
      });
      
    }
    
    return pagesHtml;
  };

  // Generate complete HTML document
  const generateJournalHTML = (fromDate, toDate, factures) => {
    const pagesContent = generatePages(factures, fromDate, toDate);
    return processTemplate(journalTemplate, {
      PAGES: pagesContent
    });
  };

  // PDF generation for preview
  const generatePdfBlob = async (htmlString) => {
    // Dynamically import libraries
    const html2canvas = await import('html2canvas').then(module => module.default);
    const jsPDF = await import('jspdf').then(module => module.jsPDF);
    
    // Create a container for the content
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.width = '210mm';
    container.innerHTML = htmlString;
    document.body.appendChild(container);
    
    try {
      // Get all pages
      const pages = container.querySelectorAll('.print-page');
      
      // Create PDF with A4 dimensions
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Process each page
      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage();
        
        const page = pages[i];
        
        // Ensure correct dimensions for capturing
        page.style.width = '210mm';
        page.style.height = '297mm';
        page.style.padding = '10mm';
        page.style.boxSizing = 'border-box';
        page.style.backgroundColor = '#FFFFFF';
        
        // Use html2canvas with optimized settings
        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#FFFFFF',
          width: page.offsetWidth,
          height: page.offsetHeight
        });
        
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297); // Full A4 page
      }
      
      return pdf.output('blob');
    } finally {
      document.body.removeChild(container);
    }
  };

  // Direct printing handler - prints without preview
  const handleDirectPrint = async () => {
    if (!dateRange[0] || !dateRange[1] || factures.length === 0) {
      return;
    }
    
    setPrintModalVisible(false);
    setIsGeneratingPdf(true);
    
    try {
      const fromDate = dateRange[0].format('DD/MM/YYYY');
      const toDate = dateRange[1].format('DD/MM/YYYY');
      
      // Generate HTML content
      const htmlContent = generateJournalHTML(fromDate, toDate, factures);
      
      // Create a hidden iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      
      document.body.appendChild(printFrame);
      
      // Write content directly to the iframe
      const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
      frameDoc.open();
      frameDoc.write(htmlContent);
      
      frameDoc.close();
      
      // Print after iframe load
      printFrame.onload = function() {
        setTimeout(() => {
          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            
            // Remove the frame after printing
            setTimeout(() => {
              document.body.removeChild(printFrame);
            }, 500);
          } catch (err) {
            console.error('Printing error:', err);
            document.body.removeChild(printFrame);
          }
        }, 300);
      };
    } catch (error) {
      console.error('Error generating print document:', error);
      Modal.error({
        title: 'Erreur',
        content: `Impossible de générer le document: ${error.message}`,
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Preview handler - shows PDF preview in modal
  const handlePreview = async () => {
    if (!dateRange[0] || !dateRange[1] || factures.length === 0) {
      return;
    }
    
    setPrintModalVisible(false);
    setIsGeneratingPdf(true);
    
    try {
      const fromDate = dateRange[0].format('DD/MM/YYYY');
      const toDate = dateRange[1].format('DD/MM/YYYY');
      
      // Generate HTML content - same as used for printing
      const htmlContent = generateJournalHTML(fromDate, toDate, factures);
      
      // Create a modal first so user sees something is happening
      const previewModalDiv = document.createElement('div');
      document.body.appendChild(previewModalDiv);
      
      // Function to close the preview
      const closePreviewModal = (pdfUrl) => {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        ReactDOM.unmountComponentAtNode(previewModalDiv);
        document.body.removeChild(previewModalDiv);
      };
      
      // Create and show an initial empty modal with loading indicator
      ReactDOM.render(
        <Modal
          title={`Journal des Ventes M3 ${dayjs().format('YYYY')}`}
          open={true}
          width="90%"
          centered={true}
          closable={true}
          maskClosable={false}
          className="pdf-preview-modal"
          style={{ top: 20, maxWidth: '1000px' }}
          footer={null}
          onCancel={() => closePreviewModal()}
        >
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              tip="Préparation du document..."
            />
          </div>
        </Modal>,
        previewModalDiv
      );
      
      // Generate PDF for preview
      const pdfBlob = await generatePdfBlob(htmlContent);
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Now update the modal with the PDF viewer
      ReactDOM.render(
        <Modal
          title={`Journal des Ventes M3 ${dayjs().format('YYYY')} - ${fromDate} au ${toDate}`}
          open={true}
          width="90%"
          centered={true}
          closable={true}
          maskClosable={true}
          className="pdf-preview-modal"
          style={{ top: 20, maxWidth: '1000px' }}
          footer={[
            <Button key="print" icon={<PrinterOutlined />} type="primary" onClick={() => {
              // Print by creating a temporary iframe with the same HTML content
              const printFrame = document.createElement('iframe');
              printFrame.style.position = 'fixed';
              printFrame.style.right = '0';
              printFrame.style.bottom = '0';
              printFrame.style.width = '0';
              printFrame.style.height = '0';
              printFrame.style.border = '0';
              
              document.body.appendChild(printFrame);
              
              // Write the same HTML content used for PDF generation
              const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
              frameDoc.open();
              frameDoc.write(htmlContent);
              
              frameDoc.close();
              
              printFrame.onload = function() {
                setTimeout(() => {
                  try {
                    printFrame.contentWindow.focus();
                    printFrame.contentWindow.print();
                    setTimeout(() => document.body.removeChild(printFrame), 500);
                  } catch (err) {
                    console.error('Print error:', err);
                    document.body.removeChild(printFrame);
                  }
                }, 300);
              };
            }}>
              Imprimer
            </Button>,
            <Button key="exit" onClick={() => closePreviewModal(pdfUrl)}>
              Sortir
            </Button>
          ]}
          onCancel={() => closePreviewModal(pdfUrl)}
        >
          <div className="pdf-preview-container" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <object
              data={pdfUrl}
              type="application/pdf"
              className="pdf-preview-object"
              style={{ 
                width: '100%', 
                height: '80vh',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              <iframe
                src={pdfUrl}
                className="pdf-preview-iframe"
                style={{ 
                  width: '100%', 
                  height: '80vh',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                title="PDF Preview"
              />
            </object>
          </div>
        </Modal>,
        previewModalDiv
      );
    } catch (error) {
      console.error('Error generating PDF preview:', error);
      Modal.error({
        title: 'Erreur',
        content: `Impossible de générer l'aperçu: ${error.message}`,
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Combined print handler - determines whether to print directly or show preview
  const handlePrint = async (isDirectPrint) => {
    if (isDirectPrint) {
      await handleDirectPrint();
    } else {
      await handlePreview();
    }
  };

  // Loading Modal
  const LoadingModal = () => (
    <Modal
      title="Génération du document"
      open={isGeneratingPdf}
      closable={false}
      maskClosable={false}
      footer={null}
    >
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spin 
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          tip="Préparation du document..."
        />
      </div>
    </Modal>
  );

  const PrintDialog = () => (
    <Modal
      title="Options d'impression"
      open={printModalVisible}
      onCancel={() => setPrintModalVisible(false)}
      footer={[
        <PermissionButton permissionModule="invoices" permissionAction="print"
          key="preview" 
          type="default" 
          icon={<EyeOutlined />}
          onClick={() => handlePrint(false)}
        >
          Aperçu
        </PermissionButton>,
        <PermissionButton permissionModule="invoices" permissionAction="print"
          key="print" 
          type="primary" 
          icon={<PrinterOutlined />}
          onClick={() => handlePrint(true)}
        >
          Imprimer
        </PermissionButton>
      ]}
      width={400}
    >
      <p>Veuillez sélectionner une option pour le Journal des Ventes M3 du {dateRange[0]?.format('DD/MM/YYYY')} au {dateRange[1]?.format('DD/MM/YYYY')}</p>
    </Modal>
  );

  // Column definitions
  const columns = [
    {
      title: 'Date Fac.',
      dataIndex: 'DATE_FAC',
      key: 'DATE_FAC',
      sorter: true,
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Ref. Fac.',
      dataIndex: 'REF_FAC',
      key: 'REF_FAC',
      sorter: true,
    },
    {
      title: 'Compte',
      dataIndex: 'COMPTE',
      key: 'COMPTE',
    },
    {
      title: 'Client',
      dataIndex: 'CLIENT',
      key: 'CLIENT',
      width: 400,
    },
    {
      title: 'Ville',
      dataIndex: ['client', 'VILLE'],
      key: 'VILLE',
    },
    {
      title: 'M paie',
      dataIndex: 'MODE_PAIE',
      key: 'MODE_PAIE',
    },
  ];

  return (
    <Card style={{ marginTop: 20, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={onClose}
          >
            Retour
          </Button>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0, marginLeft: '10px' }}>
            Journal des Ventes M3 {dayjs().format('YYYY')}
          </h2>
        </Space>
      </div>

      <Form layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item>
          <RangePicker 
            onChange={handleDateChange}
            value={dateRange}
            format="DD/MM/YYYY"
            placeholder={['Date début', 'Date fin']}
          />
        </Form.Item>
        <Form.Item>
          <Button 
            type="primary"
            icon={<ReloadOutlined />}
            onClick={generateReport}
            disabled={!dateRange[0] || !dateRange[1]}
          >
            Générer le Journal
          </Button>
        </Form.Item>
        <Form.Item>
          <PermissionButton permissionModule="invoices" permissionAction="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => setPrintModalVisible(true)}
            disabled={!showReport || factures.length === 0}
          >
            Imprimer
          </PermissionButton>
        </Form.Item>
      </Form>

      {showReport && (
        <Table
          columns={columns}
          dataSource={factures}
          rowKey="REF_FAC"
          loading={loading}
          pagination={false}
          onChange={handleTableChange}
          size="small"
          scroll={{ y: 'calc(100vh - 300px)' }}
        />
      )}
      
      <PrintDialog />
      <LoadingModal />
    </Card>
  );
};

export default FactureJournal;
